import React, { CSSProperties, useCallback } from 'react';
import { Form, FormControlProps, Stack } from 'rsuite';
import {
  CheckboxWithErrorStatus,
  CheckboxWithErrorStatusProps,
} from '../common/CheckboxWithErrorStatus';
import type { CheckType } from 'schema-typed';
import { CustomHTML } from '../common/CustomHTML';

interface AnnotatedCheckboxProps extends FormControlProps {
  title?: string;
  label: string | React.ReactNode;
  name: string;
  value?: boolean;
  onChange?: (value: boolean) => void;
  style?: CSSProperties;
  hasError?: boolean;
  validationRule?: CheckType<boolean, string>;
}

const AnnotatedCheckbox = ({
  id,
  title,
  label,
  name,
  validationRule,
  style,
  hasError,
  onChange,
  ...rest
}: AnnotatedCheckboxProps) => {
  const CheckboxWithErrorStateAndProps = useCallback(
    (props: CheckboxWithErrorStatusProps) => (
      <CheckboxWithErrorStatus
        {...props}
        showError={hasError}
      />
    ),
    [hasError]
  );

  const handleChange = (value: boolean) => {
    if (onChange) {
      onChange(value);
    }
  };

  return (
    <Form.Group
      controlId={id || name}
      style={style}
    >
      {title && <Form.ControlLabel>{title}</Form.ControlLabel>}

      <Stack
        className="mt-8"
        direction="column"
        alignItems="flex-start"
      >
        <Stack.Item>
          <Stack
            direction="row"
            alignItems="center"
            spacing={4}
          >
            <Stack.Item>
              <Form.Control
                id={id}
                accepter={CheckboxWithErrorStateAndProps}
                name={name}
                checkTrigger="none"
                rule={validationRule}
                {...rest}
                onChange={handleChange}
              />
            </Stack.Item>
            <Stack.Item className="mt-4">
              <Form.ControlLabel id={`${id}-label`}>
                {typeof label === 'string' ? (
                  <CustomHTML text={label} />
                ) : (
                  label
                )}
              </Form.ControlLabel>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    </Form.Group>
  );
};

export default AnnotatedCheckbox;
